import { useState } from "react";
import { Plus, X } from "lucide-react";

const rowClass =
  "flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5";

/** Edit a string[] stored as a newline-separated form value. */
export function StringListEditor({ value = "", onChange, placeholder = "Add an item" }) {
  const items = String(value || "")
    .split("\n")
    .map((entry) => entry.trim())
    .filter(Boolean);
  const [draft, setDraft] = useState("");

  const commit = (next) => onChange(next.join("\n"));
  const add = () => {
    const text = draft.trim();
    if (!text) return;
    commit([...items, text]);
    setDraft("");
  };
  const update = (index, text) => {
    const next = [...items];
    next[index] = text;
    commit(next);
  };
  const remove = (index) => commit(items.filter((_, i) => i !== index));

  return (
    <div className="mt-1.5 space-y-2">
      {items.map((item, index) => (
        <div key={`${item}-${index}`} className={rowClass}>
          <input
            className="min-w-0 flex-1 bg-transparent text-sm text-slate-800 outline-none"
            value={item}
            onChange={(event) => update(index, event.target.value)}
          />
          <button
            type="button"
            onClick={() => remove(index)}
            className="rounded-md p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
            aria-label="Remove item"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <div className={rowClass}>
        <input
          className="min-w-0 flex-1 bg-transparent text-sm text-slate-800 outline-none"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

const parseRows = (value) => {
  try {
    const parsed = JSON.parse(String(value || "") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Structured editor for tableOfContents: [{ title, page? }]. */
export function TableOfContentsEditor({ value = "", onChange }) {
  const rows = parseRows(value);

  const commit = (next) =>
    onChange(next.length ? JSON.stringify(next, null, 2) : "");

  const add = () => commit([...rows, { title: "" }]);
  const update = (index, key, nextValue) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, [key]: nextValue };
      if (key === "page" && nextValue === "") delete updated.page;
      return updated;
    });
    commit(next);
  };
  const remove = (index) => commit(rows.filter((_, i) => i !== index));

  return (
    <div className="mt-1.5 space-y-2">
      {rows.map((row, index) => (
        <div key={index} className={rowClass}>
          <input
            className="min-w-0 flex-1 bg-transparent text-sm text-slate-800 outline-none"
            value={row.title || ""}
            placeholder="Chapter title"
            onChange={(event) => update(index, "title", event.target.value)}
          />
          <input
            type="number"
            min="0"
            className="w-16 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 outline-none"
            value={row.page ?? ""}
            placeholder="Page"
            onChange={(event) =>
              update(index, "page", event.target.value === "" ? "" : Number(event.target.value))
            }
          />
          <button
            type="button"
            onClick={() => remove(index)}
            className="rounded-md p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
            aria-label="Remove chapter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
      >
        <Plus className="h-3.5 w-3.5" /> Add chapter
      </button>
      {!rows.length ? (
        <p className="m-0 text-xs text-slate-400">
          Add the chapters that make up this resource.
        </p>
      ) : null}
    </div>
  );
}
